import { useContext, useRef, useLayoutEffect, useEffect, Fragment } from "react";
import { Box, Button, Grid, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ACTIONS, WizardContext } from "../../wizard/Context";
import { CircularLoader } from "../Loader";
import { useQueryClient } from "react-query";
import ItemConfig from "./ItemConfig";

const SideBarContent = ({
    isFullScreen,
    setIsMenuOpen,
    isLoading,
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
}) => {
    const { state, dispatch } = useContext(WizardContext);
    const { chatConfig } = state;
    const theme = useTheme();
    const queryClient = useQueryClient();
    const selectedChatRef = useRef(null);

    // Handle chat selection
    const handleSelectChat = (chatId, title, createdAt) => {
        queryClient.cancelMutations(["sendPrompt"]);

        dispatch({
            type: ACTIONS.UPDATE_CHAT_WINDOW,
            payload: {
                chatConfig: {
                    ...chatConfig,
                    chatId,
                    title,
                    createdAt,
                    initiated: false,
                },
            },
        });

        if (!isFullScreen) {
            setIsMenuOpen(false);
        }
    };

    // Scroll to selected chat when chatConfig changes
    useLayoutEffect(() => {
        if (selectedChatRef.current) {
            selectedChatRef.current.scrollIntoView({
                block: "center",
            });
        }
    }, [chatConfig?.chatId]);

    // See More button component
    const SeeMore = () => {
        return (
            <Grid
                sx={{
                    margin: "2px",
                }}
            >
                <Button
                    sx={{
                        padding: "10px",
                        textAlign: "center",
                        color: theme.palette.primary[500],
                        borderRadius: "5px",
                        width: "100%",
                        justifyContent: "center",
                    }}
                    onClick={fetchNextPage}
                >
                    See More
                </Button>
            </Grid>
        );
    }

    // Chats Not Found component
    const ChatsNotFound = () => {
        return (
            <Grid
                sx={{
                    height: "99%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                No chats found
            </Grid>
        );
    }

    return (
        <Grid
            sx={{
                flexDirection: "column",
                width: "inherit",
                height: isFullScreen ? "calc(100vh - 96px)" : "63vh",
                scrollbarColor: `${theme.palette.primary[800]} #FFFFFF`,
                scrollbarWidth: "thin",
                overflowY: "auto",
            }}
        >
            {data?.pages?.[0]?.chats?.length === 0 && <ChatsNotFound />}
            <List sx={{
                margin: "2px",
                padding: 0
            }}>
                {data?.pages?.map((page) => (
                    <Fragment >
                        {page?.chats?.map((chat) => (
                            <ListItem
                                sx={{
                                    padding: 0,
                                    marginY: "5px",
                                    width: "inherit",
                                    '&:hover': {
                                        backgroundColor: '#e2e2e242',
                                    },
                                    borderRadius: "5px",
                                    backgroundColor:
                                        chatConfig?.chatId === chat.chatId &&
                                        theme.palette.primary[50],
                                    borderLeft:
                                        chatConfig?.chatId === chat.chatId &&
                                        `5px solid ${theme.palette.primary[500]}`,
                                }}
                                key={chat?.chatId}
                                ref={chatConfig?.chatId === chat.chatId ? selectedChatRef : null}
                            >
                                <ListItemButton
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'unset',
                                        }
                                    }}
                                    onClick={() => handleSelectChat(chat?.chatId, chat?.title, chat?.createdAt)}
                                >
                                    <ListItemText>
                                        <Box
                                            sx={{
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {chat?.title}
                                        </Box>
                                    </ListItemText>
                                </ListItemButton>
                                <ItemConfig key={chat?.chatId} chatId={chat?.chatId} chatTitle={chat?.title} />
                            </ListItem>
                        ))}
                    </Fragment>
                ))}
            </List>
            {(isFetchingNextPage || isLoading) && <CircularLoader />}
            {hasNextPage && !isFetchingNextPage && <SeeMore />}
        </Grid>
    );
};

export default SideBarContent;